import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Plus, X, ShieldCheck } from "lucide-react";
import { adminApi } from "@/lib/admin";

/**
 * Super-admin only section: manage the DB-backed full-access allowlist.
 *
 * Any email added here gets auto-elevated to admin + voice_beta +
 * direct_voice_beta on their very next authenticated request — no code
 * change or redeploy needed. Removal only takes the email off the
 * runtime list; it does NOT auto-demote the already-elevated user
 * (use the standard admin user-management panel to demote a real
 * admin), preventing accidental self-lockout.
 */
export default function SuperAdminAllowlistSection() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.listFullAccessAllowlist();
      setData(res);
      setForbidden(false);
    } catch (e) {
      if (e.status === 403) {
        setForbidden(true);
        return;
      }
      toast.error(e.message || "Failed to load allowlist");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@") || busy) return;
    setBusy(true);
    try {
      await adminApi.addFullAccessAllowlist(v);
      toast.success(`${v} will be elevated on their next request.`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (address) => {
    if (
      !confirm(
        `Remove ${address} from the runtime allowlist?\n\nNote: This will NOT demote the user if they're already an admin. Use the User Management panel to demote.`,
      )
    )
      return;
    setBusyEmail(address);
    try {
      await adminApi.removeFullAccessAllowlist(address);
      toast.success(`${address} removed from allowlist.`);
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to remove");
    } finally {
      setBusyEmail(null);
    }
  };

  if (forbidden) {
    return (
      <div
        data-testid="superadmin-allowlist-forbidden"
        className="border border-dashed border-[var(--border)] px-4 py-6 font-mono-ui text-[11px] tracking-[0.18em] uppercase text-[var(--text-dim)] text-center"
      >
        Super-admin only. Sign in as a configured super-admin account to manage this list.
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="superadmin-allowlist-loading"
        className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]"
      >
        Loading allowlist…
      </div>
    );
  }

  const items = data.items || [];

  return (
    <div data-testid="superadmin-allowlist-section" className="space-y-8">
      {/* Add form */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-1 inline-flex items-center gap-2">
          <ShieldCheck size={12} /> Grant full access
        </div>
        <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mb-5 max-w-2xl">
          Adding an email here elevates that account to <em>admin</em> plus every
          beta feature (voice, direct voice) automatically on their next
          authenticated request. No redeploy required. Super admins{" "}
          <code className="text-[var(--text-muted)]">
            {(data.super_admins || [data.super_admin]).filter(Boolean).join(", ") || "—"}
          </code>{" "}
          only.
        </p>

        <form onSubmit={onAdd} className="flex gap-2 items-stretch max-w-xl">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="grantee@example.com"
            data-testid="superadmin-allowlist-email-input"
            className="flex-1 bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 font-mono-ui text-xs text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)] transition-colors"
          />
          <button
            type="submit"
            disabled={busy || !email.includes("@")}
            data-testid="superadmin-allowlist-add-btn"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            <Plus size={12} /> {busy ? "Adding…" : "Grant"}
          </button>
        </form>
      </div>

      {/* Current list */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
          Runtime allowlist ({items.length})
        </div>
        {items.length === 0 ? (
          <div
            data-testid="superadmin-allowlist-empty"
            className="border border-dashed border-[var(--border)] px-4 py-6 font-mono-ui text-[11px] tracking-[0.18em] uppercase text-[var(--text-dim)] text-center"
          >
            No runtime grants yet.
          </div>
        ) : (
          <div className="border border-[var(--border)]">
            {items.map((row, idx) => (
              <div
                key={row.email}
                data-testid={`superadmin-allowlist-row-${row.email}`}
                className={`flex items-center justify-between px-4 py-3 ${idx > 0 ? "border-t border-[var(--border-soft)]" : ""}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <Mail size={14} className="text-[var(--text-dim)] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-mono-ui text-xs text-[var(--text)] truncate">
                      {row.email}
                    </div>
                    <div className="font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-1">
                      Granted by {row.granted_by || "—"}
                      {row.granted_at
                        ? ` · ${new Date(row.granted_at).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onRemove(row.email)}
                  disabled={busyEmail === row.email}
                  data-testid={`superadmin-allowlist-remove-${row.email}`}
                  className="font-mono-ui text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border border-[var(--border)] text-[var(--text-muted)] hover:border-red-500/60 hover:text-red-400 disabled:opacity-40 transition-colors inline-flex items-center gap-1.5"
                >
                  <X size={11} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-3">
          Removal only revokes future auto-elevation. Existing admins keep their
          role until manually demoted in User Management.
        </p>
      </div>
    </div>
  );
}
